/**
 * Account menu helpers shared with the freeze-viewer AccountMenu.
 * Mirrors lists-site site-chrome.js login / profile resolution.
 */

const LOGIN_BASE = 'https://containd.space';
const PROFILE_API = 'https://containd.space/v1/publicProfileMe';
const ENSURE_API = 'https://containd.space/v1/publicProfileEnsure';

export type AccountProfileLinks = {
  viewHref: string;
  editHref: string;
  label: 'View profile' | 'Edit profile';
  avatarUrl: string | null;
};

export function normalizeAccountLocale(locale?: string | null): 'en' | 'de' | 'jp' {
  const loc = (locale || 'en').toLowerCase();
  if (loc === 'de' || loc === 'jp') return loc;
  return 'en';
}

/** Login page URL for the given locale (no next=). */
export function accountLoginBase(locale?: string | null): string {
  return `${LOGIN_BASE}/${normalizeAccountLocale(locale)}/login/`;
}

/**
 * Sign-in URL that returns to the current share after auth.
 * `nextUrl` should be the full page URL (or path+search+hash).
 */
export function accountSignInHref(
  locale: string | null | undefined,
  nextUrl: string,
): string {
  const base = accountLoginBase(locale);
  const next = encodeURIComponent(nextUrl);
  return `${base}?next=${next}`;
}

export function accountSettingsHref(locale?: string | null): string {
  return `${accountLoginBase(locale)}#account`;
}

export async function resolveAccountProfileLinks(
  token: string,
  locale?: string | null,
): Promise<AccountProfileLinks> {
  const editHref = accountSettingsHref(locale);
  try {
    await fetch(ENSURE_API, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: '{}',
    });
    const res = await fetch(PROFILE_API, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (!res.ok) {
      return {
        viewHref: editHref,
        editHref,
        label: 'Edit profile',
        avatarUrl: null,
      };
    }
    const data = (await res.json()) as {
      profile?: { alias?: string; isPublic?: boolean; avatarUrl?: string | null };
    };
    const profile = data.profile || {};
    const alias = profile.alias;
    const isPublic = !!profile.isPublic;
    const avatarUrl = profile.avatarUrl || null;
    if (alias && isPublic) {
      return {
        viewHref: `${LOGIN_BASE}/u/${encodeURIComponent(alias)}/`,
        editHref,
        label: 'View profile',
        avatarUrl,
      };
    }
    return {
      viewHref: editHref,
      editHref,
      label: 'Edit profile',
      avatarUrl,
    };
  } catch {
    return {
      viewHref: editHref,
      editHref,
      label: 'Edit profile',
      avatarUrl: null,
    };
  }
}
